/* ============================================================================
   assets/ll-hub-advert.js
   The behaviour for sections/ll-hub-advert.liquid.
   ============================================================================

   This used to be an inline <script> in the section. Inline meant it was part
   of the HTML document: re-rendered by Shopify and re-sent to the browser on
   every single page view, never cached, and counted against the time the
   server spends building the page. On the homepage the six hub sections came
   to 127 KB of the 1.8 MB document.

   The code is byte-identical on every render, so it belongs in an asset the
   browser can cache once and reuse across the whole site.

   WHAT CHANGED, AND WHAT DID NOT. The section used to bake its own id into the
   script as `var ROOT_ID = "{{ uid }}"`, which is the single reason this could
   not be a static file. One asset now serves every instance: it finds each
   root by its [data-llha-root] marker and reads the id from
   data-section-id, which the markup already carried. Everything inside
   initInstance is the original code, unchanged.

   The per-instance JSON config block stays in the section, because that is
   real per-instance data rather than code.
============================================================================ */
(function () {
  /* Two instances of the same section put two identical <script src> tags on
     the page, and the browser runs both. */
  if (window.__llHubAdvertJS) return;
  window.__llHubAdvertJS = true;

  /* ---- block 1 of 1, extracted verbatim ---- */
  (function () {
    var inited = {};

    function initInstance(ROOT_ID) {
      function boot(root) {
          if (!root || root.dataset.llhaReady === '1') return;
          root.dataset.llhaReady = '1';

          var ads = [].slice.call(root.querySelectorAll('[data-llha-ad]'));
          if (!ads.length) return;

          /* "One Piece", "one-piece" and "onepiece" are the same game. Normalising
             both sides means a game key typed by hand in the theme editor still
             lines up with the hub's stored value. */
          function key(v) {
            return String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '');
          }

          var byGame = {}, fallback = null;
          ads.forEach(function (ad) {
            var g = key(ad.getAttribute('data-game'));
            if (!g || g === 'any') { if (!fallback) fallback = ad; return; }
            /* First block wins if two are set to the same game, rather than the
               last silently overwriting the one above it in the editor. */
            if (!byGame[g]) byGame[g] = ad;
          });

          var sel = root.getAttribute('data-hub') || '[data-tab][data-game]';
          /* Only tabs that name a game. The pre-order hub's tabs sit higher up
             the page with no data-game, and the first selected one used to
             answer for the whole page, so this fallback never saw a game. */
          var tabs = [].slice.call(document.querySelectorAll(sel)).filter(function (t) {
            return t.hasAttribute('data-game');
          });

          /* The section renders the default game's advert up front, so the space
             is held from first paint; start from whatever is already showing. */
          var current = null;
          for (var ci = 0; ci < ads.length; ci++) { if (!ads[ci].hidden) { current = ads[ci]; break; } }

          /* Release dates are printed by Liquid; re-read them here so a cached
             page still says "Out now" on the day, and counts the days honestly
             from local midnight (release day itself is "Out now"). */
          function paintDates(scope) {
            var now = new Date();
            var today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            [].forEach.call(scope.querySelectorAll('[data-llha-date]'), function (el) {
              var p = String(el.getAttribute('data-llha-date')).split('-');
              if (p.length !== 3) return;
              var day = new Date(+p[0], +p[1] - 1, +p[2]);
              var days = Math.round((day - today) / 864e5);
              var lab = el.querySelector('[data-llha-lab]');
              var when = el.querySelector('[data-llha-when]');
              if (days <= 0) {
                el.setAttribute('data-past', '1');
                if (lab) lab.textContent = 'Out now';
                if (when) when.textContent = '';
              } else {
                el.removeAttribute('data-past');
                if (lab) lab.textContent = 'Releases';
                if (when) when.textContent = days === 1 ? ' · tomorrow' : ' · in ' + days + ' days';
              }
            });
          }
          paintDates(root);

          /* The advert sits above the hub's tabs. If its height changes as a
             game is picked (or the section hides for a game with no advert),
             everything below moves, including the tab just tapped. Measure the
             active tab before and after, and scroll by whatever is left over
             once the browser's own scroll anchoring has had its say. */
          function activeTab() {
            for (var i = 0; i < tabs.length; i++) {
              if (tabs[i].getAttribute('aria-selected') === 'true') return tabs[i];
            }
            return null;
          }

          function show(game) {
            var next = byGame[key(game)] || fallback || null;
            if (next === current) return;

            var anchor = activeTab();
            var before = anchor ? anchor.getBoundingClientRect().top : null;
            var inView = before !== null && before > -40 && before < window.innerHeight;

            ads.forEach(function (ad) {
              if (ad === next) return;
              ad.hidden = true;
              ad.classList.remove('is-in');
            });

            current = next;
            root.hidden = !next;
            if (next) next.hidden = false;

            if (inView) {
              var after = anchor.getBoundingClientRect().top;
              var drift = after - before;
              if (Math.abs(drift) > 1) window.scrollBy(0, drift);
            }
            if (!next) return;

            /* Restart the fade. Removing the class and reading offsetWidth forces the
               reflow that lets the same animation run again on a re-selected tab. */
            next.classList.remove('is-in');
            void next.offsetWidth;
            next.classList.add('is-in');

            /* Clear it when this advert has no accent of its own, rather than
               leaving the last one in place. Without the else, an advert with no
               accent - the "Any game" fallback especially - kept whichever colour
               the previously selected game had set, so the same fallback rendered
               red after Magic and purple after Lorcana. */
            var acc = next.getAttribute('data-accent');
            if (acc) root.style.setProperty('--acc', acc);
            else root.style.removeProperty('--acc');
          }

          /* --- 1. the hub's own broadcast --- */
          document.addEventListener('ll:hub:game', function (e) {
            if (e && e.detail && e.detail.game) show(e.detail.game);
          });

          /* --- 2. fallback: watch the tabs directly ---
             For the window where this section is live and the hub has not been
             re-uploaded with the event in it. Cheap - aria-selected changes only
             when somebody picks a tab. */

          function readTabs() {
            var t = activeTab();
            return t ? t.getAttribute('data-game') : null;
          }

          if (tabs.length) {
            var seen = readTabs();
            if (seen) show(seen);

            if ('MutationObserver' in window) {
              var mo = new MutationObserver(function () {
                var g = readTabs();
                if (g) show(g);
              });
              tabs.forEach(function (t) {
                mo.observe(t, { attributes: true, attributeFilter: ['aria-selected'] });
              });
            }
          } else {
            /* No hub on this page: keep the default advert the section rendered,
               or show the fallback if there is one. */
            if (!current) show(null);
          }
        }

        function find() { return document.querySelector('.llha-' + ROOT_ID + '[data-llha-root]'); }

        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', function () { boot(find()); });
        } else {
          boot(find());
        }

        /* Theme editor: the section is re-rendered on every settings change, and the
           hub may be re-rendered under it, so the tab observers have to be rebuilt. */
        document.addEventListener('shopify:section:load', function (e) {
          if (e.detail && e.detail.sectionId && String(e.detail.sectionId) !== String(ROOT_ID)) {
            var live = find();
            if (live) { live.dataset.llhaReady = ''; boot(live); }
            return;
          }
          boot(find());
        });
    }

    function initOnce(id) {
      if (!id || inited[id]) return;
      inited[id] = true;
      initInstance(id);
    }

    function bootAll() {
      var roots = document.querySelectorAll("[data-llha-root][data-section-id]");
      for (var i = 0; i < roots.length; i++) initOnce(roots[i].getAttribute("data-section-id"));
    }

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", bootAll);
    } else {
      bootAll();
    }

    /* A section added in the theme editor arrives after first boot. Each
       instance registers its own load/unload handlers inside initInstance,
       so this only has to catch ids that have never been seen. */
    document.addEventListener("shopify:section:load", bootAll);
    document.addEventListener("shopify:section:unload", function (e) {
      var id = e && e.detail && String(e.detail.sectionId);
      if (id) delete inited[id];
    });
  })();
})();
